//
// An Entry pane shows a grid of entries, and has a spinner state.
// It responds to events from the entry pane data component
// to enable loading partials in via ajax
//

import { component as defineComponent } from 'flightjs';
import withChildComponents from 'flight-with-child-components';
import withLoadingSpinner from 'app/mixins/with-loading-spinner';
import DetachedDropdown from '@99designs/common/jquery-plugins/detached-dropdown';
import SocialMediaShareButtons from 'contest/components/social-media-share-buttons';
import EntryThumbnailUI from 'contest/ui/entry-thumbnail';
import EntryPaneAnchorsUI from 'contest/ui/entry-pane-anchors';
import EntryActionsUI from 'entry/ui/actions';
import EntryActionsData from 'entry/data/actions';
import EntryLikeButtonUI from 'entry/ui/like-button';
import EntryLikeData from 'entry/data/like';
import EntryStatusUI from 'entry/ui/entry-status';
import EntryRatingUI from 'entry/ui/rating';
import ContestItemSelectionsUI from 'contest/ui/contest-item-selections';
import SelectContestItemsButtonUI from 'contest/ui/select-item-button';
import EntryAwardButtonUI from 'contest/ui/entry-award-button';
import EntryReportButtonUI from 'contest/ui/entry-report-button';
import EliminateDesignerButtonUI from 'contest/ui/eliminate-designer-button';
import StyledSelectUI from 'app/ui/styled-select';
import BroadcastingInputUI from 'contest/ui/broadcasting-input';
import FilterCriteriaUpdaterData from 'contest/data/filter-criteria-updater';
import updateDesignCollectionReactData from 'contest/ui/update-design-collection-react-data';
import PromotedUpgradeUI from 'contest/ui/promoted-upgrade';
import PromotedUpgradeData from 'contest/data/promoted-upgrade';
import Collapsible from 'contest/ui/collapsible';
import React from 'react';
import ReactDOM from 'react-dom';
import app from 'contest/data/app';
import { FluxibleComponent } from 'fluxible-addons-react';
import { IntlProvider } from 'react-intl';
import ConnectedContestsFinalistButton from 'contest/apps/entries/components/connected/connected-contest-finalist-button';
import ConnectedSelectWinnerButton from 'contest/apps/entries/components/connected/connected-select-winner-button';
import GroupByDesignerControl from 'contest/apps/entries/components/group-by-designer-control';
import {
    NewDesignsInfoBox,
    GoodDesignsInfoBox,
    BriefScoreInfoBox,
    FinalRoundInfoBox,
    FinalistButtonOnboardingPrompt,
    GroupByPotentialFinalistPrompt,
} from 'contest/components/onboarding-prompts';
import ActiveUserFlagStore from 'contest/stores/active-user-flag-store';
import OnboardingPromptStore from 'contest/stores/onboarding-prompt-store';
import ContestsActiveUserStore from 'contest/stores/ContestsActiveUserStore';
import SelectedWinnersStore from 'contest/apps/entries/stores/selected-winners-store';
import EntryDesignerTimezone from 'contest/ui/entry-designer-timezone';
import DdoLink from 'contest/ui/ddo-link';

var entryPane = function () {
    this.childTeardownEvent = 'teardownChildComponents';
    this.dropdowns = [];
    this.boundModalTriggers = [];

    this.attributes({
        entryPaneSelector: '[data-entry-pane]',
        modalButtonSelector: '[data-modal-name]',
        tooltipSelector: '[data-tooltip]',

        // Exclude entry comment dropdown, it's initialised manually for finer control
        dropdownSelector: '[data-detached-dropdown]',

        context: false,
        intl: false,
        finalistButton: '[data-finalist-button]',
        winnerButton: '[data-winner-button]',
        groupByDesignerControl: '[data-group-by-designer-control]',
    });

    this.after('initialize', function () {
        this.initializeLoadingSpinner(this.select('entryPaneSelector'), null, 'entry-pane');
        this.initChildComponents();

        this.on('dataEntryPaneLoading', this.showLoadingState);
        this.on('dataEntryPaneContentReceived', this.replaceEntryPaneContent);
        this.on('dataEntryPaneLoadingFinished', this.hideLoadingState);
    });

    this.initChildComponents = function () {
        this.enableDropdowns();

        this.attachChild(EntryThumbnailUI, '[data-entry]');
        this.attachChild(EntryPaneAnchorsUI, '[data-entry-pane-anchors]');
        this.attachChild(EntryActionsData, '[data-entry]');
        this.attachChild(EntryActionsUI, '[data-entry]');
        this.attachChild(EntryLikeButtonUI, '[data-entry]');
        this.attachChild(EntryLikeData, '[data-entry]');
        this.attachChild(EntryStatusUI, '[data-entry]');
        this.attachChild(EntryRatingUI, '[data-entry-rating]');
        this.attachChild(Collapsible, '[data-designer-entries]');
        this.attachChild(EntryDesignerTimezone, '[data-designer-timezone]');
        this.attachChild(DdoLink, '[data-entry-image]');
        const entryShareNodes = document.querySelectorAll('[data-entry-share]');
        if (entryShareNodes.length > 0) {
            entryShareNodes.forEach((entryShareNode) => {
                const intl = JSON.parse(entryShareNode.dataset.intl);
                const shareURLs = JSON.parse(entryShareNode.dataset.shareUrls || '{}');
                ReactDOM.render(
                    <IntlProvider locale={document.documentElement.lang} messages={intl.messages}>
                        <SocialMediaShareButtons
                            shareURLs={shareURLs}
                            trackEventOptions={{
                                share_subject_type: 'entry',
                                location: 'contests',
                            }}
                        />
                    </IntlProvider>,
                    entryShareNode
                );
            });
        }

        if (this.attr.context) {
            // Onboarding prompts for "Mark as finalist" button
            this.select('finalistButton').each((i, node) => {
                const state = JSON.parse(node.dataset.state);
                const user = {
                    userId: parseInt(state.userId, 10),
                    avatarUrl: state.avatarUrl,
                    displayName: state.displayName,
                };
                ReactDOM.render(
                    <FluxibleComponent context={app.getContext().getComponentContext()}>
                        <IntlProvider locale={document.documentElement.lang} {...this.attr.intl}>
                            {i === 0 ? (
                                <FinalistButtonOnboardingPrompt>
                                    <ConnectedContestsFinalistButton
                                        tooltip={state.tooltipText}
                                        user={user}
                                    />
                                </FinalistButtonOnboardingPrompt>
                            ) : (
                                <ConnectedContestsFinalistButton
                                    tooltip={state.tooltipText}
                                    user={user}
                                />
                            )}
                        </IntlProvider>
                    </FluxibleComponent>,
                    node
                );
            });

            // Onboarding prompt for "group by designer" button,
            // to be shown if stage is `selecting-finalists`
            // and entry pane is still in grid view
            this.select('groupByDesignerControl').each((i, node) => {
                ReactDOM.render(
                    <FluxibleComponent context={app.getContext().getComponentContext()}>
                        <IntlProvider locale={document.documentElement.lang} {...this.attr.intl}>
                            <GroupByPotentialFinalistPrompt>
                                <GroupByDesignerControl
                                    title={
                                        this.attr.intl.messages['group_by_designer_button_title']
                                    }
                                    selected={node.getAttribute('data-selected-value')}
                                />
                            </GroupByPotentialFinalistPrompt>
                        </IntlProvider>
                    </FluxibleComponent>,
                    node
                );
            });

            app.registerStore(SelectedWinnersStore);
            this.select('winnerButton').each((i, node) => {
                const state = JSON.parse(node.dataset.state);
                ReactDOM.render(
                    <FluxibleComponent context={app.getContext().getComponentContext()}>
                        <ConnectedSelectWinnerButton entry={state} tooltip={state.tooltipText} />
                    </FluxibleComponent>,
                    node
                );
            });
        } else {
            this.attachChild(ContestItemSelectionsUI, '[data-contest-finalists]', {
                dataFailedToAddItemEvent: 'dataFailedToAddFinalist',
                dataItemsLoadedEvent: 'dataFinalistsLoaded',
                uiRemoveItemEvent: 'uiDesignerUnselected',
                uiAddItemEvent: 'uiDesignerSelected',
            });
            this.attachChild(SelectContestItemsButtonUI, '[data-select-contest-finalist-button]', {
                dataFailedToAddItemEvent: 'dataFailedToAddFinalist',
                dataItemsLoadedEvent: 'dataFinalistsLoaded',
                dataItemRemovedEvent: 'dataFinalistRemoved',
                dataItemAddedEvent: 'dataFinalistAdded',
                uiRemoveItemEvent: 'uiDesignerUnselected',
                uiAddItemEvent: 'uiDesignerSelected',
            });
        }

        this.attachChild(EntryAwardButtonUI, '[data-entry-award-button]');
        this.attachChild(EntryReportButtonUI, '[data-entry-report-button]');
        this.attachChild(EliminateDesignerButtonUI, '[data-eliminate-designer-button]');
        this.attachChild(StyledSelectUI, '[data-styled-select]');
        this.attachChild(BroadcastingInputUI, '[data-broadcasting-input]', {
            broadcastEvent: 'uiFilterCriteriaChanged',
        });
        this.attachChild(FilterCriteriaUpdaterData, '[data-filter-criteria-updater]', {
            listenEvent: 'uiFilterCriteriaChanged',
            eventToTrigger: 'uiEntryPaneCriteriaChanged',
        });

        this.select('entryPaneSelector')
            .find(this.attr.modalButtonSelector)
            .each((i, elem) => {
                const $elem = $(elem);
                const name = $elem.data('modalName');
                if (!this.boundModalTriggers.includes(name)) {
                    $elem.modal();
                    this.boundModalTriggers.push(name);
                }
            });

        this.select('entryPaneSelector')
            .find(this.attr.tooltipSelector)
            .each((i, elem) => $(elem).tooltip());

        this.attachChild(updateDesignCollectionReactData, '[data-design-collection]');
        this.attachChild(PromotedUpgradeUI, '[data-promoted-upgrade]');
        this.attachChild(PromotedUpgradeData, '[data-promoted-upgrade]');

        const infoBoxNode = document.querySelector('[data-contest-infobox]');
        if (infoBoxNode) {
            const intl = JSON.parse(infoBoxNode.dataset.intl);
            app.registerStore(ActiveUserFlagStore);
            app.registerStore(OnboardingPromptStore);
            app.registerStore(ContestsActiveUserStore);
            const context = app.getContext();
            context.getStore(ActiveUserFlagStore).initFromActiveUser(window.activeUser);
            const state = JSON.parse(infoBoxNode.dataset.state || '{}');
            const listingId = infoBoxNode.dataset.listingId;

            const InfoBoxComponent = {
                NewDesignsInfoBox: NewDesignsInfoBox,
                GoodDesignsInfoBox: GoodDesignsInfoBox,
                BriefScoreInfoBox: BriefScoreInfoBox,
                FinalRoundInfoBox: FinalRoundInfoBox,
            }[infoBoxNode.dataset.component];

            if (InfoBoxComponent) {
                ReactDOM.render(
                    <FluxibleComponent context={context.getComponentContext()}>
                        <IntlProvider
                            locale={document.documentElement.lang}
                            messages={intl.messages}
                        >
                            <InfoBoxComponent {...state} promptKeySuffix={listingId} />
                        </IntlProvider>
                    </FluxibleComponent>,
                    infoBoxNode
                );
            }
        }
    };

    this.replaceEntryPaneContent = function (e, data) {
        if (typeof data.html !== 'undefined' && data.html.length > 0) {
            this.clearDropdowns();
            this.trigger(this.childTeardownEvent);
            this.select('entryPaneSelector').html(data.html);
            this.initChildComponents();
        }
    };

    this.clearDropdowns = function () {
        this.dropdowns.forEach(function (dropdown) {
            dropdown.teardown();
        });

        this.dropdowns = [];
    };

    this.enableDropdowns = function () {
        this.select('entryPaneSelector')
            .find(this.attr.dropdownSelector)
            .each(
                function (i, el) {
                    this.dropdowns.push(new DetachedDropdown($(el)));
                }.bind(this)
            );
    };

    this.showLoadingState = function () {
        this.trigger('uiSpinnerShow', { id: 'entry-pane' });
    };

    this.entryPaneSelectorIsInViewport = function () {
        return window.pageYOffset < this.select('entryPaneSelector').offset().top;
    };

    this.hideLoadingState = function (e, data) {
        // scroll up to entryPane
        if (!data.noscroll && !this.entryPaneSelectorIsInViewport()) {
            this.select('entryPaneSelector').get(0).scrollIntoView();
        }
        this.trigger('uiSpinnerHide', { id: 'entry-pane' });
    };
};

export default defineComponent(entryPane, withChildComponents, withLoadingSpinner);



// WEBPACK FOOTER //
// ./src/web/js/contest/ui/entry-pane.js